import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/alert-messages';
const HTTP_METHOD = 'get';

export type MakeGetAlertMessagesDeps<AlertMessageIndexSchema extends Schema.Top> = {
  readonly alertMessageIndex: AlertMessageIndexSchema;
};

export const makeGetAlertMessages = <AlertMessageIndexSchema extends Schema.Top>(
  deps: MakeGetAlertMessagesDeps<AlertMessageIndexSchema>
) => {
  const ListAlertMessagesError = createBackendApiError({
    domain: 'AlertMessage',
    endpoint: 'List',
    path: ENDPOINT,
    identifierField: 'page',
  });

  const listAlertMessagesFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({});
  });

  const listAlertMessages = () =>
    listAlertMessagesFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.alertMessageIndex))(response),
          404: (response) =>
            Effect.fail(new ListAlertMessagesError.NotFoundError({ page: 1, response })),
          orElse: (response) =>
            Effect.fail(new ListAlertMessagesError.ApiRequestError({ page: 1, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.AlertMessagesList,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} alert messages`
        )
      ),
      Effect.withSpan('api.alertMessages.list', {
        kind: 'internal',
      })
    );

  return { ListAlertMessagesError, listAlertMessages };
};
